import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { format } from "date-fns";
import { CalendarIcon, Clock, Globe, CheckCircle2 } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  BUSINESS_TZ,
  getLocalTimezone,
  getSlotsForDate,
  isoCalendarDate,
  formatInTz,
} from "@/lib/booking";
import BookingReassurance from "@/components/BookingReassurance";
import Honeypot from "@/components/Honeypot";
import DiscoveryCallAgenda from "@/components/DiscoveryCallAgenda";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const SERVICE_OPTIONS = [
  "Cold Calling",
  "Appointment Setting",
  "Acquisition Manager",
  "Virtual Assistant",
  "CRM Follow-Up",
  "Not Sure Yet",
] as const;

const bookingSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().min(5, "Phone is required").max(40),
  serviceInterest: z.enum(SERVICE_OPTIONS, { errorMap: () => ({ message: "Pick what you're interested in" }) }),
  details: z.string().trim().max(1500).optional().or(z.literal("")),
  consent: z.literal(true, { errorMap: () => ({ message: "Please agree to be contacted" }) }),
});

const BookAppointment = () => {
  const localTz = useMemo(() => getLocalTimezone(), []);
  // Preselect today so visitors immediately see open times instead of an empty form.
  const [date, setDate] = useState<Date | undefined>(() => new Date());
  const [slot, setSlot] = useState<Date | null>(null);
  const [bookedSet, setBookedSet] = useState<Set<number>>(new Set());
  const [blockedDates, setBlockedDates] = useState<Set<string>>(new Set());
  const [blockedSlots, setBlockedSlots] = useState<Set<number>>(new Set());
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    serviceInterest: "" as (typeof SERVICE_OPTIONS)[number] | "",
    details: "",
    consent: false,
  });
  const [hp, setHp] = useState("");

  // Load blocked dates/slots once
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("blocked_slots").select("blocked_date, slot_start");
      const dates = new Set<string>();
      const slots = new Set<number>();
      (data ?? []).forEach((b: { blocked_date: string; slot_start: string | null }) => {
        if (b.slot_start) slots.add(new Date(b.slot_start).getTime());
        else dates.add(b.blocked_date);
      });
      setBlockedDates(dates);
      setBlockedSlots(slots);
    })();
  }, []);

  // Load booked slots for the chosen date
  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    setSlot(null);
    const slots = getSlotsForDate(date);
    const from = slots[0];
    const to = new Date(slots[slots.length - 1].getTime() + 30 * 60_000);
    supabase
      .rpc("get_booked_slots", { _from: from.toISOString(), _to: to.toISOString() })
      .then(({ data }) => {
        const set = new Set<number>();
        (data ?? []).forEach((r: { slot_start: string }) => set.add(new Date(r.slot_start).getTime()));
        setBookedSet(set);
        setLoadingSlots(false);
      });
  }, [date]);

  const slotsForDay = useMemo(() => (date ? getSlotsForDate(date) : []), [date]);

  const dateBlocked = date ? blockedDates.has(isoCalendarDate(date)) : false;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hp.trim()) { setDone(true); return; }
    if (!slot) {
      toast({ title: "Pick a time", description: "Please select an available time slot." });
      return;
    }
    const parsed = bookingSchema.safeParse(form);
    if (!parsed.success) {
      const first = Object.values(parsed.error.flatten().fieldErrors)[0]?.[0];
      toast({ title: "Check your details", description: first ?? "Please complete all required fields.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("appointments").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone,
      company: null,
      business_type: parsed.data.serviceInterest,
      notes: parsed.data.details || `Service interest: ${parsed.data.serviceInterest}`,
      slot_start: slot.toISOString(),
      visitor_timezone: localTz,
      status: "pending",
    });
    setSubmitting(false);
    if (error) {
      const friendly =
        error.code === "23505"
          ? "That slot was just taken. Please pick another time."
          : "We couldn't book that appointment. Please try again or email abdelrahaman@elevateglobalsolutions.net.";
      toast({ title: "Booking failed", description: friendly, variant: "destructive" });
      return;
    }
    setDone(true);
  };

  if (done && slot) {
    return (
      <div className="container-narrow px-4 py-20 md:px-8">
        <Helmet>
          <title>Booking Confirmed | Elevate Global Solutions</title>
        </Helmet>
        <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-10 text-center">
          <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-primary" />
          <h1 className="text-2xl font-bold">You're booked!</h1>
          <p className="mt-3 text-muted-foreground">
            We've received your appointment for{" "}
            <strong className="text-foreground">
              {formatInTz(slot, localTz, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </strong>{" "}
            ({localTz}). We'll reach out shortly to confirm.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-narrow px-4 py-12 md:px-8 md:py-16">
      <Helmet>
        <title>Book a Free Strategy Call | Elevate Global Solutions</title>
        <meta name="description" content="Book a free 30-minute, no-obligation strategy call. We respond within 1 business day. US real estate acquisition specialists." />
        <meta property="og:title" content="Book a Free Strategy Call | Elevate Global Solutions" />
        <meta property="og:description" content="Free 30-minute strategy call. We'll review your market, buy box, and pipeline gaps." />
        <meta property="og:url" content="https://www.elevateglobalsolutions.net/book" />
        <meta property="og:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:title" content="Book a Free Strategy Call | Elevate Global Solutions" />
        <meta name="twitter:description" content="Free 30-minute strategy call. We'll review your market, buy box, and pipeline gaps." />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/book" />
      </Helmet>

      <div className="mx-auto max-w-3xl">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-foreground">
          Free · 30 min · No obligation
        </span>
        <h1 className="mt-4 font-display text-3xl font-bold md:text-4xl">Book a free strategy call</h1>
        <p className="mt-2 text-muted-foreground">
          30-minute call with our team. We'll review your market, buy box, and pipeline gaps, and
          email you a custom acquisition plan within 48 hours, whether you hire us or not. Available
          <strong> 9 AM, 5 PM Eastern Time</strong>, seven days a week. Times shown in your local
          timezone (<strong>{localTz}</strong>).
        </p>

        <div className="mt-8">
          <DiscoveryCallAgenda />
        </div>


        <form onSubmit={onSubmit} className="mt-8 space-y-6 relative" noValidate>
          <Honeypot value={hp} onChange={setHp} />
          {/* Date + slot picker */}
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Date (US Eastern)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (d < today) return true;
                      const max = new Date();
                      max.setDate(max.getDate() + 60);
                      if (d > max) return true;
                      return blockedDates.has(isoCalendarDate(d));
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Globe className="h-3.5 w-3.5" /> Your timezone
              </Label>
              <div className="flex h-10 items-center rounded-md border border-input bg-muted/40 px-3 text-sm">
                {localTz}
              </div>
            </div>
          </div>

          {date && (
            <div>
              <Label className="mb-2 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> Available times
              </Label>
              {dateBlocked ? (
                <p className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                  This date is unavailable. Please pick another day.
                </p>
              ) : loadingSlots ? (
                <p className="text-sm text-muted-foreground">Loading times…</p>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {slotsForDay.map((s) => {
                    const taken = bookedSet.has(s.getTime()) || blockedSlots.has(s.getTime());
                    const past = s.getTime() < Date.now();
                    const disabled = taken || past;
                    const selected = slot?.getTime() === s.getTime();
                    return (
                      <Button
                        key={s.getTime()}
                        type="button"
                        variant="outline"
                        disabled={disabled}
                        onClick={() => setSlot(s)}
                        className={cn(
                          "rounded-md border px-3 py-2 text-sm transition-colors",
                          selected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-background hover:bg-accent",
                          disabled && "cursor-not-allowed opacity-40 hover:bg-background",
                        )}
                      >
                        {formatInTz(s, localTz, {
                          ...(isoCalendarDate(s) !== isoCalendarDate(date) ? { month: "short", day: "numeric" } : {}),
                          hour: "numeric", minute: "2-digit",
                        })}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Booking details */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Full name *</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input id="phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            </div>
            <div className="space-y-3 md:col-span-2">
              <Label>What are you interested in? *</Label>
              <RadioGroup
                value={form.serviceInterest}
                onValueChange={(v) => setForm({ ...form, serviceInterest: v as (typeof SERVICE_OPTIONS)[number] })}
                className="grid gap-2 sm:grid-cols-2"
              >
                {SERVICE_OPTIONS.map((opt) => (
                  <Label
                    key={opt}
                    htmlFor={`svc-${opt}`}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-background p-3 text-sm font-normal transition-colors hover:border-secondary/60",
                      form.serviceInterest === opt && "border-primary bg-accent/40",
                    )}
                  >
                    <RadioGroupItem id={`svc-${opt}`} value={opt} />
                    <span className="text-foreground">{opt}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="details" className="flex items-center gap-2">
                Anything we should know before the call?
                <span className="text-xs font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="details"
                rows={3}
                placeholder="Market, lead source, current dialer/CRM, biggest bottleneck, only if helpful."
                value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
              />
            </div>
            <div className="md:col-span-2 flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
              <input
                id="consent"
                type="checkbox"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                className="mt-1 h-4 w-4 cursor-pointer accent-primary"
                required
              />
              <Label htmlFor="consent" className="cursor-pointer text-sm font-normal leading-relaxed text-muted-foreground">
                I agree to be contacted by Elevate Global Solutions about my inquiry by email, phone, or SMS. We use industry-standard security practices to protect your information. We never sell your personal data and do not share it with third parties for advertising or marketing purposes. Information submitted through our website is used only to provide services, respond to inquiries, improve website performance, and support legitimate business operations.
              </Label>
            </div>
          </div>

          <Button type="submit" disabled={submitting || !slot} className="w-full md:w-auto">
            {submitting ? "Booking…" : "Confirm booking"}
          </Button>
          <p className="text-xs text-muted-foreground">
            Business hours: 9 AM, 5 PM {BUSINESS_TZ.replace("_", " ")} (US Eastern). Slots automatically convert to your local time.
          </p>
        </form>
      </div>

      <div className="mx-auto mt-12 max-w-5xl">
        <BookingReassurance />
      </div>
    </div>
  );
};

export default BookAppointment;
